import { Stack, Typography } from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColumns,
  type GridRowParams,
  GridOverlay
} from '@mui/x-data-grid'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import PageSection from 'components/common/PageSection'
import { SearchField } from 'components/common/SearchField'
import { getPresetKeyFromURL } from 'components/scenarios/create/status-checks/third-party-presets'
import { StatusCheckListActionCell } from 'components/status-checks/list/StatusCheckListActionCell'
import { StatusCheckListNameCell } from 'components/status-checks/list/StatusCheckListNameCell'
import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  isCustomStatusCheck,
  STATUS_CHECK_CATEGORIES_LOOKUP
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import useSearch from 'hooks/use-search'
import { muiTheme } from 'mui-theme'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { type StatusCheck } from 'types/api/status-checks/status-check'
import { naturalSortBy } from 'utils/list'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'
import { messageFromResponseError } from 'utils/strings'

function searchMapFn(statusCheck: StatusCheck) {
  // add status check type to searchable items list
  const statusCheckType = isCustomStatusCheck(statusCheck)
    ? 'custom'
    : getPresetKeyFromURL(statusCheck.endpointConfiguration?.url)
  return [statusCheck.name, statusCheckType].filter(Boolean)
}

const EmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No Health Checks</Typography>
    </GridOverlay>
  )
}

const columnConfig: GridColumns<StatusCheck> = [
  {
    ...defaultColumnProps,
    field: 'name',
    headerName: 'Name',
    flex: 3,
    renderCell: ({ row }) => <StatusCheckListNameCell statusCheck={row} />
  },
  {
    ...defaultColumnProps,
    field: 'category',
    headerName: 'Category',
    flex: 1,
    maxWidth: 120,
    renderCell: ({ row }) => {
      const categoryKey = row.category

      return (
        <Typography>{STATUS_CHECK_CATEGORIES_LOOKUP[categoryKey]}</Typography>
      )
    }
  },
  {
    ...defaultColumnProps,
    field: '_actions',
    headerName: 'Actions',
    flex: 1,
    minWidth: 300,
    renderCell: ({ row }) => <StatusCheckListActionCell statusCheck={row} />
  }
]

// TODO add tests
export const StatusCheckList: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const navigate = useNavigate()

  const {
    data: statusChecks,
    isFetching: isFetchingStatusChecks,
    isError: hasErrorStatusChecks,
    isSuccess: isSuccessStatusChecks,
    error: statusChecksError
  } = useGetStatusChecks({ staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME })

  const searchState = useStatusChecksSearchContext()
  const { searchValue, setSearchValue, pageNum, setPageNum } =
    searchState.statusChecks

  const sortedStatusChecks = useMemo(
    () =>
      isSuccessStatusChecks ? naturalSortBy(statusChecks, 'name', 'asc') : [],
    [isSuccessStatusChecks, statusChecks]
  )

  const searchStatusChecks = useSearch(sortedStatusChecks, searchMapFn)

  const filteredStatusChecks = useMemo(
    () => searchStatusChecks(searchValue ?? ''),
    [searchValue, searchStatusChecks]
  )

  const handleSearchInputChange = (value: string) => {
    setSearchValue(String(value))
  }

  const handleRowClick: DataGridProps['onRowClick'] = (
    params: GridRowParams<StatusCheck>,
    event
  ) => {
    const statusCheck = params.row
    safeRowClick(event, () => {
      navigate(statusChecksUrls.edit(statusCheck.identifier))
    })
  }

  return (
    <PageSection
      data-testid="status-checks-list-page"
      errorMessage={messageFromResponseError(
        statusChecksError,
        'There was an issue while retrieving Health Checks'
      )}
      hasError={hasErrorStatusChecks}
      isLoading={isFetchingStatusChecks}
      isLoadingOverlayFullScreen={false}
      loadingBoxProps={{ padding: muiTheme.spacing(12) }}
    >
      <Stack direction="row">
        <SearchField
          data-test-status-check-list-search
          disabled={isFetchingStatusChecks}
          label="Search"
          size="small"
          value={searchValue}
          onChange={handleSearchInputChange}
        />
      </Stack>
      <DataGrid<StatusCheck>
        {...defaultGridProps}
        columns={columnConfig}
        components={{ NoRowsOverlay: EmptyState }}
        getRowId={(row) => row.identifier}
        page={pageNum}
        rows={filteredStatusChecks}
        sx={getGridStyles({ clickableRow: true })}
        onPageChange={setPageNum}
        onRowClick={handleRowClick}
      />
    </PageSection>
  )
}
